import { useState, useEffect, useMemo, useRef } from "react";
import Swal from "sweetalert2";
import {
  GetRevenueSummaryByBrand,
  RevenueSummaryByBrandItem,
} from "../services/revenueservice";
import { fetchdropdownMall } from "../services/dropdown";
import { MONTHS_OPTIONS } from "../utils/";

interface SelectOption {
  value: string;
  label: string;
}

export const INITIAL_MONTH = MONTHS_OPTIONS[new Date().getMonth()];
export const INITIAL_YEAR = String(new Date().getFullYear());

export function useBrandTable() {
  const [selectedMall, setSelectedMall] = useState("");
  const [mallOptions, setMallOptions] = useState<SelectOption[]>([]);
  const isMallDisabled = true;
  const [selectedMonth, setSelectedMonth] = useState(INITIAL_MONTH);
  const [selectedYear, setSelectedYear] = useState(INITIAL_YEAR);
  const [searchQuery, setSearchQuery] = useState("");
  const [data, setData] = useState<RevenueSummaryByBrandItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const currentPageRef = useRef(1);
  const rowsPerPageRef = useRef(10);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetchdropdownMall(0);
        if (!res?.dropdownClassDatas) return;

        const options: SelectOption[] = res.dropdownClassDatas.map(
          (m: { DataId: number; DataLabel: string }) => ({ value: m.DataId.toString(), label: m.DataLabel }),
        );

        if (options.length === 1) {
          setMallOptions(options);
          setSelectedMall(options[0].value);
          localStorage.setItem("mall_id", options[0].value);
        } else {
          setMallOptions([{ value: "0", label: "All" }]);
          setSelectedMall("0");
        }
      } catch {
        Swal.fire({ icon: "error", title: "Error", text: "Failed to fetch mall options" });
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!selectedMonth || !selectedYear) {
      setData([]);
      return;
    }

    const load = async () => {
      setIsLoading(true);
      setData([]);
      try {
        const monthIndex = MONTHS_OPTIONS.indexOf(selectedMonth) + 1;
        const res = await GetRevenueSummaryByBrand(
          parseInt(selectedYear, 10),
          monthIndex,
        );
        const rows =
          (res as { summaryByBrandDatas?: RevenueSummaryByBrandItem[] })?.summaryByBrandDatas ??
          (res as { Content?: { summaryByBrandDatas?: RevenueSummaryByBrandItem[] } })?.Content?.summaryByBrandDatas ??
          [];
        setData(rows);
      } catch {
        Swal.fire({ icon: "error", title: "Error", text: "Failed to fetch summary data" });
        setData([]);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [selectedMonth, selectedYear]);

  const handlePageChange = (page: number) => {
    currentPageRef.current = page;
  };

  const handleRowsPerPageChange = (newPerPage: number, page: number) => {
    rowsPerPageRef.current = newPerPage;
    currentPageRef.current = page;
  };

  const filteredData = useMemo(
    () =>
      data.filter((item) =>
        item.MallName.toString()
          .toLowerCase()
          .includes(searchQuery.toLowerCase()),
      ),
    [data, searchQuery],
  );

  const totalAmount = useMemo(
    () => filteredData.reduce((sum, r) => sum + r.Amount, 0),
    [filteredData],
  );

  const totalTransactions = useMemo(
    () => filteredData.reduce((sum, r) => sum + r.TotalData, 0),
    [filteredData],
  );

  const emptyMessage = !selectedMonth
    ? "Please select a month to view data"
    : !selectedYear
      ? "Please select a year to view data"
      : "There are no records to display";

  return {
    mallOptions,
    selectedMall,
    isMallDisabled,
    selectedMonth,
    setSelectedMonth,
    selectedYear,
    setSelectedYear,
    searchQuery,
    setSearchQuery,
    filteredData,
    isLoading,
    totalAmount,
    totalTransactions,
    emptyMessage,
    currentPageRef,
    rowsPerPageRef,
    handlePageChange,
    handleRowsPerPageChange,
  };
}